import { LucideIcon } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent } from '../ui/Card';

export interface QuickAction {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  description?: string;
  badge?: string | number;
  isAvailable?: boolean;
}

interface QuickActionCardProps {
  title?: string;
  actions: QuickAction[];
  columns?: 2 | 3 | 4;
  className?: string;
}

export function QuickActionCard({
  title,
  actions,
  columns = 3,
  className = '',
}: QuickActionCardProps) {
  const navigate = useNavigate();

  const gridCols = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
  };

  const availableActions = actions.filter((a) => a.isAvailable !== false);

  if (availableActions.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {title && (
        <h3 className="text-lg font-semibold text-[#EBFFEE] mb-4">{title}</h3>
      )}
      <div className={`grid ${gridCols[columns]} gap-4`}>
        {availableActions.map((action) => {
          const Icon = action.icon;
          return (
            <Card
              key={action.id}
              className={`
                bg-[#221F1F] border-[#504A4A] cursor-pointer
                transition-all duration-200 hover:border-[#9B1421] hover:shadow-lg
                ${action.isAvailable === false ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              onClick={() => {
                if (action.isAvailable !== false && action.path) {
                  navigate(action.path);
                }
              }}
            >
              <CardContent className="!p-4">
                <div className="flex flex-col items-center text-center gap-2">
                  <div className="relative">
                    <div className="p-3 bg-[#363232] rounded-lg">
                      <Icon className="w-6 h-6 text-text-primary" />
                    </div>
                    {action.badge !== undefined && (
                      <span className="absolute -top-2 -right-2 px-1.5 py-0.5 bg-[#9B1421] text-white text-xs font-bold rounded-full min-w-[20px] text-center">
                        {action.badge}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#EBFFEE]">{action.label}</p>
                    {action.description && (
                      <p className="text-xs text-white/80 mt-1">{action.description}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

